import { memo, useState, useCallback } from 'react';
import { CaseReportOffendersList } from '@/components/casereport/CaseReportOffendersList';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { MultiSelect } from '@/components/MultiSelect';
import { App } from '@/services/app-compat';
import { CaseRpt } from '@/services/case-report-service';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getInsecureUUID = _wg('getInsecureUUID');

function emptyOffenderForm() {
  return { tempid: null as any, personId: null as any, personName: '', arrested: null as any, offenses: [] as any[], notes: '' };
}

interface Props {
  offenders: any[];
  setOffenders: (o: any[]) => void;
  involvedPersonOptions: { id: any; name: string }[];
  offenseOptions: any[];
  isReadOnly: boolean;
  officerLabel: string;
}

export const CaseReportOffenseSection = memo(function CaseReportOffenseSection({
  offenders, setOffenders, involvedPersonOptions, offenseOptions, isReadOnly, officerLabel,
}: Props) {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyOffenderForm);

  const openModal = useCallback((offender?: any) => {
    if (offender) {
      setForm({
        tempid: offender.tempid, personId: offender.npsdPerson,
        personName: offender.personName || '',
        arrested: offender.arrested ? String(offender.arrested) : null,
        offenses: offender.offenses || [], notes: offender.officerNotes || '',
      });
    } else {
      setForm(emptyOffenderForm());
    }
    setShowModal(true);
  }, []);

  const saveOffender = useCallback(() => {
    if (!form.personId) { App.showPopupNotification('Person is required.', 'error'); return; }
    if (!form.arrested) { App.showPopupNotification('Arrested status is required.', 'error'); return; }
    if (!form.offenses?.length) { App.showPopupNotification('At least one offense is required.', 'error'); return; }

    const dup = CaseRpt.currentMDTRpt.offenders.findIndex((n: any) =>
      n.npsdPerson?.toString() === form.personId.toString() && (!form.tempid || n.tempid !== form.tempid));
    if (dup > -1) { App.showPopupNotification('This person has already been added as an offender.', 'warning'); return; }

    const entry = {
      tempid: form.tempid || getInsecureUUID(), npsdPerson: form.personId,
      personName: form.personName, arrested: parseInt(form.arrested),
      offenses: form.offenses.slice(), officerNotes: form.notes,
    };

    if (form.tempid) {
      const idx = CaseRpt.currentMDTRpt.offenders.findIndex((o: any) => o.tempid === form.tempid);
      if (idx > -1) CaseRpt.currentMDTRpt.offenders[idx] = entry;
    } else {
      CaseRpt.currentMDTRpt.offenders.push(entry);
    }
    setOffenders(CaseRpt.currentMDTRpt.offenders.slice());
    setShowModal(false);
    setForm(emptyOffenderForm());
  }, [form, setOffenders]);

  return (
    <>
      <div className="tab-panel">
        <CaseReportOffendersList
          offenders={offenders}
          involvedPersons={involvedPersonOptions}
          isReadOnly={isReadOnly}
          onChange={(o: any[]) => { CaseRpt.currentMDTRpt.offenders = o; setOffenders(o); }}
          onAdd={() => openModal()}
          onEdit={(item: any) => openModal(item)}
        />
      </div>

      <ConfirmModal
        open={showModal}
        title={form.tempid ? 'Edit Offender' : 'Add Offender'}
        confirmText={form.tempid ? 'Update Offender' : 'Add Offender'}
        confirmClass="primary"
        dialogClass="kstc-dialog--form"
        onConfirm={saveOffender}
        onCancel={() => { setShowModal(false); setForm(emptyOffenderForm()); }}
      >
        <div className="form-group">
          <label className="mandatory"><b>Person</b></label>
          <Dropdownlist value={form.personId} items={involvedPersonOptions}
            labelKey="name" valueKey="id" placeholder="Select person..."
            onChange={(val) => {
              const p = involvedPersonOptions.find((o: any) => o.id === val);
              setForm(f => ({ ...f, personId: val, personName: p?.name || '' }));
            }} />
        </div>
        <div className="form-group">
          <label className="mandatory"><b>Arrested?</b></label>
          <div className="radio-group">
            <label className="radio-inline"><input type="radio" checked={form.arrested === '1'} onChange={() => setForm(f => ({ ...f, arrested: '1' }))} /> Yes</label>
            <label className="radio-inline"><input type="radio" checked={form.arrested === '2'} onChange={() => setForm(f => ({ ...f, arrested: '2' }))} /> No</label>
            <label className="radio-inline"><input type="radio" checked={form.arrested === '3'} onChange={() => setForm(f => ({ ...f, arrested: '3' }))} /> N/A</label>
          </div>
        </div>
        <div className="form-group">
          <label className="mandatory"><b>Offenses</b></label>
          <MultiSelect value={form.offenses} items={offenseOptions}
            labelKey="value" valueKey="code" placeholder="Select offenses..."
            onChange={(val: any[]) => setForm(f => ({ ...f, offenses: val }))} />
        </div>
        <div className="form-group">
          <label><b>{officerLabel} Notes</b></label>
          <textarea className="form-control" value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={5} />
        </div>
      </ConfirmModal>
    </>
  );
});
